import { StringKeys } from "./utility/typescriptTypes";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";

export class MDetailFormConfig {
  config!: IMDetailFormConfig[];
  constructor(config: IMDetailFormConfig[]) {
    this.config = config;
  }

  getConfigKeys(): string[] {
    return this.config.map(c => String(c.key));
  }

  getConfigByKey(key: string) {
    return this.config.find(c => c.key === key);
  }

  getConfigValueByKey<T>(key: string): T {
    return this.getConfigByKey(key)?.model;
  }

  set configValueByKey(newValue: { key: string; value: any }) {
    const found = this.getConfigByKey(newValue.key);
    if (found) {
      found.model = newValue.value;
    }
  }
}

export interface IMDetailFormConfig<T = any> {
  /**
   * category of the value; groups the values by category, e.g adress oder personal information
   */
  category: string;

  /**
   * key to identify this config. must be unique
   */
  key: StringKeys<T>;

  /**
   * keywords for the search of the form
   */
  searchKeywords: string[];

  /**
   * name of the component that should be rendered
   */
  type: DetailFormComponentsEnum;

  /**
   * v-model of the component that shoud be rendered
   */
  model?: any;

  /**
   * The Property of the component that are bind via v-bind to the template
   */
  props?: any;

  /**
   * Render form element if model of key matches value
   */
  condition?: {
    key: string;
    value: string;
  };
}

export interface IFormableClass {
  formables: IMDetailFormConfig[];
}

type IIsFormableConfiguration = Omit<Omit<IMDetailFormConfig, "key">, "type"> & {
  type: DetailFormComponentsEnum | IFormableClass;
};

/**
 * Class decorator that adds a getter for searchable properties of the class
 *
 * @param constructor
 * @returns
 */
function IsFormable<T extends { new (...args: any[]): {} }>(constructor: T) {
  return class extends constructor {
    /**
     * List of searchable properties of the class
     */
    static get formables(): IMDetailFormConfig[] {
      return constructor.prototype.formables ?? [];
    }

    constructor(...args: any[]) {
      super(...args);

      // formables getter is not enumerable
      Object.defineProperty(constructor, "formables", {
        enumerable: false
      });
    }
  };
}

/**
 * Property decorator that adds a property to the prototype that contains the formables
 * method-decorator @see https://www.typescriptlang.org/docs/handbook/decorators.html#method-decorators
 *
 * @param config
 * @returns
 */
function FormConfig(config: IIsFormableConfiguration) {
  return function(target: any, propertyKey: string) {
    if (!target.formables) {
      target.formables = [];
    }

    if (Object.values(DetailFormComponentsEnum).includes(config.type as DetailFormComponentsEnum)) {
      // add the searchable to the formables array of the object
      target.formables.push({ ...config, key: propertyKey });
    } else {
      // in case we have a nesting add the nested formables to the formables array of the parent
      (config.type as IFormableClass).formables.forEach(nestedSearchableConfig => {
        const nestedKey = `${propertyKey}.${nestedSearchableConfig.key}`;
        target.formables.push({ ...nestedSearchableConfig, key: nestedKey });
      });
    }
  };
}

/**
 * Apply type of IsFormable class
 *
 * @see IsFormable
 */
class Form {
  private static assertIsFormableClass<T extends Function>(c: T): asserts c is T & IFormableClass {
    if (!((c as any) as IFormableClass).formables) {
      ((c as any) as IFormableClass).formables = [];
    }
  }
  /**
   * Apply type of IsFormable class
   *
   * @see IsFormable
   */
  static createForClass<Class extends Function>(c: Class): Class & IFormableClass {
    this.assertIsFormableClass(c);
    return c;
  }
}

export { Form, IsFormable, FormConfig };
